import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';
import { useUserActionLogger } from '../../hooks/useUserActionLogger';
import { AnimatedBackground } from '../ui/AnimatedBackground';
import { Button } from '../ui/Button';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useUserActionLogger({
    autoTrackPageView: true,
    pageId: 'not_found_page',
    componentName: 'NotFoundPage'
  });

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:p-6 relative overflow-hidden">
      <AnimatedBackground />

      <div
        className={`relative z-10 w-full max-w-[420px] transition-all duration-500 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="text-center px-2">
          <div className="flex items-center justify-center gap-2 sm:gap-2.5 mb-8 sm:mb-10 animate-float">
            <div className="relative w-9 h-9 sm:w-10 sm:h-10 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md shadow-neutral-900/5 border border-neutral-200/60">
              <img src={skylarqLogo} alt="Skylarq" className="w-6 h-6 sm:w-7 sm:h-7 object-contain opacity-90" />
            </div>
            <span className="text-[20px] sm:text-[22px] font-semibold text-neutral-700 tracking-tight">
              Skylarq
            </span>
          </div>

          <div className="mb-6 sm:mb-8">
            <h1 className="text-[80px] sm:text-[100px] md:text-[120px] font-serif font-normal text-neutral-800 leading-none tracking-tight mb-2">
              404
            </h1>
            <h2 className="text-[20px] sm:text-[24px] font-medium text-neutral-600 mb-3 sm:mb-4">
              Oops, we lost this one
            </h2>
            <p className="text-[14px] sm:text-[15px] text-neutral-400 leading-relaxed max-w-[320px] mx-auto">
              Sorry about that! The page you were looking for seems to have wandered off. Let's get you back on track.
            </p>
          </div>

          <div className="flex flex-col gap-2.5 sm:gap-3 max-w-[280px] sm:max-w-[300px] mx-auto">
            <Button
              id="not-found-go-home"
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => navigate('/datasets')}
              leftIcon={<Home className="w-4 h-4" />}
              aria-label="Go to Dashboard"
            >
              Go to Dashboard
            </Button>

            <Button
              id="not-found-go-back"
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              aria-label="Go Back"
            >
              Go Back
            </Button>
          </div>

          <p className="mt-8 sm:mt-10 text-[12px] text-neutral-400">
            Still stuck?{' '}
            <a
              href="mailto:support@skylarq.ai"
              className="text-neutral-500 hover:text-neutral-700 underline transition-colors duration-200"
            >
              We're here to help
            </a>
          </p>
        </div>
      </div>

      <div
        className={`fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 sm:gap-4 z-10 transition-all duration-700 delay-300 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <a href="/privacy" className="text-[10px] sm:text-xs text-neutral-400 hover:text-neutral-600 transition-colors duration-200">
          Privacy
        </a>
        <span className="text-neutral-300">·</span>
        <a href="/terms" className="text-[10px] sm:text-xs text-neutral-400 hover:text-neutral-600 transition-colors duration-200">
          Terms
        </a>
        <span className="text-neutral-300">·</span>
        <span className="text-[10px] sm:text-xs text-neutral-400">© {new Date().getFullYear()} Skylarq</span>
      </div>
    </div>
  );
};
